import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useState } from 'react';
import { formatDate, localizedValue } from './nationalizationViewModel';

const INITIAL_FORM = {
  establishmentId: '',
  ruleId: '',
  sourceSystem: '',
  officialAsOf: '',
  resultType: 'percentage',
  statusCode: '',
  ratePct: '',
  count: '',
  requiredCount: '',
  band: '',
  reference: '',
  notes: ''
};

export default function NationalizationReconciliation({
  establishments = [],
  obligations = [],
  language = 'en',
  latestReconciliation,
  selectedEstablishmentId,
  saving,
  error,
  success,
  t,
  onSave
}) {
  useUiLanguage();
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    establishmentId: selectedEstablishmentId || establishments[0]?.id || '',
    ruleId: obligations[0]?.rule_id || ''
  }));

  useEffect(() => {
    setForm((current) => ({
      ...current,
      establishmentId: selectedEstablishmentId || current.establishmentId || establishments[0]?.id || '',
      ruleId: current.ruleId || obligations[0]?.rule_id || ''
    }));
  }, [establishments, obligations, selectedEstablishmentId]);

  const selectedObligation = obligations.find((obligation) => obligation.rule_id === form.ruleId);
  const isNitaqatResult = String(selectedObligation?.code || '').startsWith('sa.nitaqat.');
  const isSaudiProfile = establishments.some((establishment) => establishment.country_code === 'SA');

  useEffect(() => {
    if (!isNitaqatResult) return;
    setForm((current) => ({
      ...current,
      sourceSystem: current.sourceSystem || 'Qiwa'
    }));
  }, [isNitaqatResult]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = () => {
    const numberOrUndefined = (value) => value === '' ? undefined : Number(value);
    onSave({
      establishment_id: form.establishmentId,
      rule_id: form.ruleId,
      rule_version: selectedObligation?.rule_version || '',
      source_system: form.sourceSystem,
      official_as_of: form.officialAsOf,
      official_result: {
        status_code: form.statusCode || undefined,
        rate_pct: (isNitaqatResult || form.resultType === 'percentage') ? numberOrUndefined(form.ratePct) : undefined,
        count: !isNitaqatResult && form.resultType === 'count' ? numberOrUndefined(form.count) : undefined,
        required_count: !isNitaqatResult && form.resultType === 'count' ? numberOrUndefined(form.requiredCount) : undefined,
        band: (isNitaqatResult || form.resultType === 'band') ? form.band || undefined : undefined
      },
      evidence: {
        reference: form.reference || undefined,
        notes: form.notes || undefined,
        // This workflow records what the employer sees in an official portal;
        // it does not pretend that JOBSEEQR fetched or verified the record.
        entry_method: 'employer_entered'
      }
    });
  };

  const hasOfficialValue = Boolean(
    form.statusCode
    || (isNitaqatResult && (form.ratePct !== '' || form.band.trim()))
    || (!isNitaqatResult && form.resultType === 'percentage' && form.ratePct !== '')
    || (!isNitaqatResult && form.resultType === 'count' && (form.count !== '' || form.requiredCount !== ''))
    || (!isNitaqatResult && form.resultType === 'band' && form.band.trim())
  );
  const canSubmit = Boolean(
    form.establishmentId
    && form.ruleId
    && form.sourceSystem.trim()
    && form.officialAsOf
    && form.reference.trim()
    && hasOfficialValue
  );

  return (
    <section className="nationalization-support-card nationalization-reconciliation">
      <div className="nationalization-support-card-header">
        <div>
          <h3>{t('reconciliation.title', 'Official record comparison')}</h3>
          <p>{t('reconciliation.description', 'Record the result shown in the employer’s official government report or portal and compare it with JOBSEEQR.')}</p>
        </div>
        <button type="button" className="nationalization-secondary-button" onClick={() => setExpanded((value) => !value)}>
          {expanded ? t('actions.hideForm', 'Hide form') : t('actions.addOfficialResult', 'Add official result')}
        </button>
      </div>

      {latestReconciliation ? (
        <div className="nationalization-latest-reconciliation">
          <i className="fa-solid fa-building-columns" aria-hidden="true" />
          <div>
            <strong>{t('reconciliation.latestStatus', 'Latest official comparison')}</strong>
            <p>{t('reconciliation.latestMeta', '{{source}} · as of {{date}}', {
              source: latestReconciliation.source_system || '—',
              date: formatDate(latestReconciliation.official_as_of, language)
            })}</p>
          </div>
          <span>{t(
            `reconciliation.status.${latestReconciliation.official_result?.status_code || latestReconciliation.comparison?.comparison_status || 'recorded'}`,
            latestReconciliation.official_result?.status_code || latestReconciliation.comparison?.comparison_status || 'Recorded'
          )}</span>
        </div>
      ) : (
        <p className="nationalization-empty-copy">
          {t('reconciliation.empty', 'This JOBSEEQR estimate has not been compared with an official employer record yet.')}
        </p>
      )}

      {expanded && (
        <div className="nationalization-reconciliation-form">
          <div className="nationalization-inline-note">
            <i className="fa-solid fa-shield-halved" aria-hidden="true" />
            {t('reconciliation.noCredentials', 'Never enter a government portal password. Only add the result and report reference.')}
          </div>
          <div className="nationalization-form-grid">
            <label className="nationalization-form-field">
              <span>{t('reconciliation.fields.rule', 'Applicable legal requirement')}</span>
              <select value={form.ruleId} onChange={(event) => setField('ruleId', event.target.value)}>
                <option value="">{t('reconciliation.placeholders.rule', 'Select a requirement')}</option>
                {obligations.map((obligation) => (
                  <option key={`${obligation.rule_id}-${obligation.rule_version}`} value={obligation.rule_id}>
                    {localizedValue(obligation, language, 'title') || obligation.rule_id}
                    {obligation.rule_version ? ` · ${obligation.rule_version}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="nationalization-form-field">
              <span>{isSaudiProfile
                ? t('reconciliation.fields.qiwaEntity', 'Qiwa entity')
                : t('reconciliation.fields.establishment', 'Company / branch')}</span>
              <select value={form.establishmentId} onChange={(event) => setField('establishmentId', event.target.value)}>
                {establishments.map((establishment) => (
                  <option key={establishment.id} value={establishment.id}>{establishment.name || establishment.id}</option>
                ))}
              </select>
            </label>
            <label className="nationalization-form-field">
              <span>{t('reconciliation.fields.sourceSystem', 'Government source')}</span>
              <input value={form.sourceSystem} onChange={(event) => setField('sourceSystem', event.target.value)} placeholder={t('reconciliation.placeholders.sourceSystem', 'Example: Qiwa or MoHRE')} />
            </label>
            <label className="nationalization-form-field">
              <span>{t('reconciliation.fields.officialAsOf', 'Official result date')}</span>
              <input type="date" value={form.officialAsOf} onChange={(event) => setField('officialAsOf', event.target.value)} />
            </label>
            {!isNitaqatResult && (
              <label className="nationalization-form-field">
                <span>{t('reconciliation.fields.resultType', 'Result type')}</span>
                <select value={form.resultType} onChange={(event) => setField('resultType', event.target.value)}>
                  <option value="percentage">{t('obligationTypes.percentage', 'Percentage')}</option>
                  <option value="count">{t('obligationTypes.count', 'Employee count')}</option>
                  <option value="band">{t('obligationTypes.band', 'Band / level')}</option>
                </select>
              </label>
            )}
            {(isNitaqatResult || form.resultType === 'percentage') && (
              <label className="nationalization-form-field">
                <span>{t('reconciliation.fields.ratePct', 'Official rate (%)')}</span>
                <input type="number" min="0" step="0.1" value={form.ratePct} onChange={(event) => setField('ratePct', event.target.value)} />
              </label>
            )}
            {!isNitaqatResult && form.resultType === 'count' && (
              <>
                <label className="nationalization-form-field">
                  <span>{t('reconciliation.fields.count', 'Official qualifying count')}</span>
                  <input type="number" min="0" value={form.count} onChange={(event) => setField('count', event.target.value)} />
                </label>
                <label className="nationalization-form-field">
                  <span>{t('reconciliation.fields.requiredCount', 'Official required count')}</span>
                  <input type="number" min="0" value={form.requiredCount} onChange={(event) => setField('requiredCount', event.target.value)} />
                </label>
              </>
            )}
            {(isNitaqatResult || form.resultType === 'band') && (
              <label className="nationalization-form-field">
                <span>{t('reconciliation.fields.band', 'Official band / level')}</span>
                {isNitaqatResult ? (
                  <select value={form.band} onChange={(event) => setField('band', event.target.value)}>
                    <option value="">{t('reconciliation.placeholders.band', 'Select the band shown in Qiwa')}</option>
                    {['red', 'low_green', 'medium_green', 'high_green', 'platinum'].map((band) => (
                      <option key={band} value={band}>{t(`nitaqatBands.${band}`, band)}</option>
                    ))}
                  </select>
                ) : (
                  <input value={form.band} onChange={(event) => setField('band', event.target.value)} />
                )}
              </label>
            )}
            <label className="nationalization-form-field">
              <span>{t('reconciliation.fields.statusCode', 'Official status')}</span>
              <select value={form.statusCode} onChange={(event) => setField('statusCode', event.target.value)}>
                <option value="">{t('common.notProvided', 'Not provided')}</option>
                {['compliant', 'not_compliant', 'provisional', 'official_band', 'not_applicable'].map((statusCode) => (
                  <option key={statusCode} value={statusCode}>
                    {t(`status.${statusCode}`, statusCode)}
                  </option>
                ))}
              </select>
            </label>
            <label className="nationalization-form-field">
              <span>{t('reconciliation.fields.reference', 'Report / certificate reference')}</span>
              <input value={form.reference} onChange={(event) => setField('reference', event.target.value)} />
            </label>
            <label className="nationalization-form-field nationalization-form-field-wide">
              <span>{t('reconciliation.fields.notes', 'Notes')}</span>
              <textarea value={form.notes} onChange={(event) => setField('notes', event.target.value)} rows="3" />
            </label>
          </div>
          {error && <p className="nationalization-form-error">{uiText(error)}</p>}
          {success && <p className="nationalization-form-success">{success}</p>}
          <div className="nationalization-form-actions">
            <button type="button" className="nationalization-primary-button" disabled={!canSubmit || saving} onClick={submit}>
              {saving ? t('actions.saving', 'Saving...') : t('actions.saveOfficialResult', 'Save official result')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
